/**
 * <SentTab> — owner-side list of shares the caller has created.
 *
 * `external-sharing` Phase 3 Session 3.3 (2026-05-09).
 *
 * 4 states: loading (3 row-shaped skeletons), error (retry CTA), empty
 * ("Share your first deal" → /builder), success (status-grouped list:
 * Active → Pending → Revoked, each group sorted by createdAt DESC).
 *
 * Highlight scroll: when `highlightId` matches a row, scroll it into view
 * after a 50ms defer (mirrors `<SignalAnchorListener>`'s pattern). Bell-
 * click navigates here with `?highlight=<latest-unread>` (Session 3.1).
 */
import { useEffect } from 'react';
import { useSentShares, useRevokeShare } from '@/hooks/use-shares';
import type { SharedLinkResponse } from '@/lib/shares-api';
import { SentShareRow, SENT_SHARE_ROW_GRID } from './SentShareRow';
import { SharesEmptyState, SharesLoadingSkeleton } from './SharesEmptyState';
import { SharesTableHeader } from './SharesTableHeader';

const SENT_COLUMNS = [
  { label: 'Recipient' },
  { label: 'Status' },
  // Views cell renders the number left-aligned (existing behavior) so the
  // header lines up over the digit. If the cell ever moves to right-aligned
  // tabular numerals, flip this to `align: 'right'` in the same diff.
  { label: 'Views' },
  { label: 'Created' },
  { label: '' }, // actions column — buttons speak for themselves
];

export interface SentTabProps {
  highlightId: string | undefined;
}

interface GroupedShares {
  active: SharedLinkResponse[];
  pending: SharedLinkResponse[];
  revoked: SharedLinkResponse[];
}

function groupByStatus(shares: SharedLinkResponse[]): GroupedShares {
  const groups: GroupedShares = { active: [], pending: [], revoked: [] };
  for (const s of shares) groups[s.status].push(s);
  for (const k of Object.keys(groups) as Array<keyof GroupedShares>) {
    groups[k].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  return groups;
}

export function SentTab({ highlightId }: SentTabProps) {
  const { data, isLoading, isError, refetch } = useSentShares();
  const revokeMutation = useRevokeShare();

  // Scroll highlighted row into view on mount + whenever the data resolves
  // and the highlight target is now present.
  useEffect(() => {
    if (!highlightId || !data) return;
    if (!data.find((s) => s.id === highlightId)) return;
    const id = window.setTimeout(() => {
      const el = document.getElementById(`share-row-${highlightId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return () => window.clearTimeout(id);
  }, [highlightId, data]);

  if (isLoading) {
    return <SharesLoadingSkeleton testid="shares-sent-loading" />;
  }

  if (isError) {
    return (
      <SharesEmptyState
        variant={{ kind: 'sent-error', onRetry: () => void refetch() }}
      />
    );
  }

  const shares = data ?? [];

  if (shares.length === 0) {
    return <SharesEmptyState variant={{ kind: 'sent-empty' }} />;
  }

  const groups = groupByStatus(shares);
  const handleRevoke = (shareId: string): void => {
    revokeMutation.mutate(shareId);
  };

  return (
    <div data-testid="shares-sent-list" className="flex flex-col gap-6">
      <ShareGroup slug="active" label="Active" shares={groups.active} highlightId={highlightId} onRevoke={handleRevoke} />
      <ShareGroup slug="pending" label="Awaiting signup" shares={groups.pending} highlightId={highlightId} onRevoke={handleRevoke} />
      <ShareGroup slug="revoked" label="Revoked" shares={groups.revoked} highlightId={highlightId} onRevoke={handleRevoke} />
    </div>
  );
}

interface ShareGroupProps {
  /**
   * Stable status-derived id used for testids + DOM hooks. Decoupled from
   * `label` so display copy can change without breaking selectors.
   */
  slug: 'active' | 'pending' | 'revoked';
  label: string;
  shares: SharedLinkResponse[];
  highlightId: string | undefined;
  onRevoke: (shareId: string) => void;
}

function ShareGroup({ slug, label, shares, highlightId, onRevoke }: ShareGroupProps) {
  if (shares.length === 0) return null;
  return (
    <div data-testid={`shares-group-${slug}`}>
      <h2 className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px] uppercase mb-2">
        {label} <span className="text-text-dim">({shares.length})</span>
      </h2>
      <SharesTableHeader
        gridClass={SENT_SHARE_ROW_GRID}
        columns={SENT_COLUMNS}
        testid={`shares-group-${slug}-header`}
      />
      <div className="flex flex-col gap-2 mt-2">
        {shares.map((share) => (
          <SentShareRow
            key={share.id}
            share={share}
            highlighted={share.id === highlightId}
            onRevoke={onRevoke}
          />
        ))}
      </div>
    </div>
  );
}
